import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useClientAuth, formatApiError } from '../../context/ClientAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { User, Eye, EyeSlash } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function ClientLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useClientAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome to your portal!');
      navigate('/portal/dashboard');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-8">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="w-6 h-6 text-primary" weight="bold" />
          </div>
          <div className="text-center">
            <span className="text-2xl font-semibold tracking-tight block" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Client Portal
            </span>
            <span className="text-sm text-muted-foreground">Applegrow Financial Advisors</span>
          </div>
        </div>

        <Card className="border border-border">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="heading-3">Welcome back</CardTitle>
            <CardDescription>Sign in to access your documents and files</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="client-login-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="client-login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="client-toggle-password"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="client-login-submit"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">First time here? </span>
              <span className="text-muted-foreground">Use the invite link sent by your accountant.</span>
            </div>

            <div className="mt-4 pt-4 border-t border-border text-center">
              <Link to="/login" className="text-sm text-muted-foreground hover:text-primary">
                Accountant login →
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
